import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaEye, FaDownload, FaTimes, FaFileAlt } from 'react-icons/fa';
import { loadRecordPreview, safeRecordFileUrl } from '../utils/recordFilePreview';
import './ManagementPolish.css';
import api from '../api';
import resolveDownloadPath from '../utils/downloads';

const fileErrorMessage = error => error?.response?.status === 403
  ? 'Você não tem permissão para acessar este arquivo.'
  : error?.response?.status === 404 ? 'Arquivo não encontrado. Verifique se ele ainda está disponível.'
  : 'Não foi possível carregar o arquivo. Tente novamente.';

const authenticatedFetcher = async (url, { signal } = {}) => {
  try {
    const response = await api.get(url, { responseType: 'blob', signal });
    return { ok: true, headers: { get: key => response.headers?.get?.(key) ?? response.headers?.[key] ?? null }, blob: async () => response.data };
  } catch (error) { throw new Error(fileErrorMessage(error)); }
};

export function RecordFilePreview({ href, name, onClose, authenticated = false, onDownload, downloading = false, downloadError = '' }) {
  const id = useId();
  const dialog = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const previousFocus = document.activeElement;
    const parentDialog = previousFocus?.closest?.('[role="dialog"]');
    const oldInert = parentDialog?.inert;
    const oldHidden = parentDialog?.getAttribute('aria-hidden');
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const escape = event => {
      if (event.key !== 'Escape') return;
      event.preventDefault(); event.stopPropagation(); closeRef.current();
    };
    document.addEventListener('keydown', escape);
    dialog.current?.focus();
    if (parentDialog) { parentDialog.inert = true; parentDialog.setAttribute('aria-hidden', 'true'); }
    return () => {
      document.removeEventListener('keydown', escape);
      document.body.style.overflow = overflow;
      if (parentDialog) {
        parentDialog.inert = oldInert;
        if (oldHidden === null) parentDialog.removeAttribute('aria-hidden');
        else parentDialog.setAttribute('aria-hidden', oldHidden);
      }
      if (previousFocus?.isConnected) previousFocus.focus?.();
    };
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    let disposed = false;
    let objectUrl;
    setLoading(true); setPreview(null); setError('');
    loadRecordPreview(href, name, { signal: controller.signal, ...(authenticated ? { fetcher: authenticatedFetcher } : {}) }).then((result) => {
      if (disposed) return;
      objectUrl = URL.createObjectURL(result.blob);
      setPreview({ kind: result.kind, url: objectUrl });
    }).catch((failure) => {
      if (!disposed) setError(failure?.message || 'N\u00e3o foi poss\u00edvel visualizar este arquivo.');
    }).finally(() => { if (!disposed) setLoading(false); });
    return () => { disposed = true; controller.abort(); if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [href, name, authenticated]);
  const keys = (event) => {
    // Portals bubble through React: do not trigger the underlying editor's focus trap.
    event.stopPropagation();
    if (event.key === 'Escape') { event.preventDefault(); onClose(); return; }
    if (event.key !== 'Tab') return;
    const nodes = Array.from(dialog.current?.querySelectorAll('button:not(:disabled), a[href], iframe, [tabindex="0"]') || []).filter((node) => node.getClientRects().length);
    const first = nodes[0]; const last = nodes[nodes.length - 1];
    if (!first) { event.preventDefault(); dialog.current?.focus(); }
    else if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };
  return createPortal(<div className="management-polish__preview-backdrop" onKeyDown={keys} onMouseDown={(event) => { event.stopPropagation(); if (event.target === event.currentTarget) onClose(); }}>
    <section ref={dialog} className="management-polish__preview" role="dialog" aria-modal="true" aria-labelledby={id} tabIndex={-1}>
      <header><div><span>Visualizar arquivo</span><h2 id={id}>{name}</h2></div><button type="button" className="management-polish__close" aria-label={'Fechar visualiza\u00e7\u00e3o'} onClick={onClose}><FaTimes aria-hidden="true" /></button></header>
      <div className="management-polish__preview-body" aria-busy={loading}>
        {loading && <p role="status">Carregando arquivo...</p>}
        {error && <div className="management-polish__preview-empty" role="status"><FaFileAlt aria-hidden="true" /><p>{error}</p></div>}
        {!error && preview?.kind === 'image' && <img src={preview.url} alt={name} onError={() => setError('A imagem n\u00e3o p\u00f4de ser exibida. Use Baixar.')} />}
        {!error && preview?.kind === 'pdf' && <object tabIndex={0} data={preview.url} type="application/pdf" aria-label={`Pr\u00e9via de ${name}`}><p>{'Este navegador n\u00e3o exibe PDF nesta janela. Use Baixar.'}</p></object>}
      </div>
      <footer>{downloadError && <p role="alert">{downloadError}</p>}<button type="button" onClick={onClose}>Fechar</button>{onDownload ? <button type="button" onClick={onDownload} disabled={downloading}><FaDownload aria-hidden="true" />{downloading ? 'Baixando...' : 'Baixar'}</button> : <a href={href} download={name} target="_blank" rel="noopener noreferrer"><FaDownload aria-hidden="true" />Baixar</a>}</footer>
    </section>
  </div>, document.body);
}
export default function RecordFileActions({ url, file, name }) {
  const href = safeRecordFileUrl(url || file?.dataUrl || file?.downloadPath || file?.downloadUrl || file?.url);
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  useEffect(() => { setOpen(false); setDownloadError(''); }, [href, name]);
  // Use the record's authorized content route before legacy form links. Never
  // send bearer credentials to an external origin or reuse form editing tokens.
  const storedUrl = safeRecordFileUrl(file?.downloadPath || file?.downloadUrl || file?.url || href);
  const resolvedStoredUrl = storedUrl ? resolveDownloadPath(storedUrl) : '';
  const trustedStoredUrl = /^\/(?!\/)/.test(resolvedStoredUrl);
  const direct = href && /^(data|blob):/i.test(href);
  let requestPath = '';
  if (file && !direct) {
    if (trustedStoredUrl) requestPath = resolvedStoredUrl;
    else if (!storedUrl && file.relativePath) requestPath = `/files?path=${encodeURIComponent(file.relativePath)}&name=${encodeURIComponent(name || 'Arquivo')}`;
  }
  if (/\/api\/?$/i.test(String(api?.defaults?.baseURL || '')) && /^\/api(\/|$)/i.test(requestPath)) {
    requestPath = requestPath.replace(/^\/api(?=\/|$)/i, '') || '/';
  }
  const download = async () => {
    if (downloading || !requestPath) return;
    setDownloading(true); setDownloadError('');
    try {
      const response = await api.get(requestPath, { responseType: 'blob' });
      const objectUrl = URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = objectUrl; anchor.download = name || 'Arquivo';
      document.body.appendChild(anchor); anchor.click(); anchor.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    } catch (error) { setDownloadError(fileErrorMessage(error)); }
    finally { setDownloading(false); }
  };
  const previewHref = requestPath || href;
  if (!previewHref) return null;
  return <>
    <button type="button" className="management-polish__file-action" title="Visualizar" aria-label={`Visualizar ${name}`} onClick={() => { setDownloadError(''); setOpen(true); }}><FaEye aria-hidden="true" /><span>Visualizar</span></button>
    {requestPath ? <button type="button" className="management-polish__file-action" title="Baixar" aria-label={`Baixar ${name}`} onClick={download} disabled={downloading}><FaDownload aria-hidden="true" /><span>{downloading ? 'Baixando...' : 'Baixar'}</span></button> : <a className="management-polish__file-action" href={href} download={name} target="_blank" rel="noopener noreferrer" title="Baixar" aria-label={`Baixar ${name}`}><FaDownload aria-hidden="true" /><span>Baixar</span></a>}
    {downloadError && !open && <p className="record-ui__file-error" role="alert">{downloadError}</p>}
    {open && <RecordFilePreview href={previewHref} name={name} authenticated={Boolean(requestPath)} onClose={() => setOpen(false)} onDownload={requestPath ? download : undefined} downloading={downloading} downloadError={downloadError} />}
  </>;
}
